import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
/** Sort key → the API sort alias declared in HashtagResource::sorts(). */
const SORTS = {
  popular: '-postCount',
  recent: '-lastUsedAt',
  alphabetical: 'name'
};

/**
 * `/hashtags` — browse what people are actually using.
 *
 * Freeform hashtags only pay off if they converge, and convergence needs two
 * things: autocomplete while writing (see HashtagMention) and a way to see
 * what already exists. This is the second one.
 */
export default class HashtagsPage extends Page {
  hashtags = [];
  loading = true;
  sort = 'popular';
  query = '';

  /** Guards against an earlier slow response overwriting a later fast one. */
  requestId = 0;
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(extractText(app.translator.trans('ernestdefoe-hashtags.forum.browse.title')));
    this.load();
  }
  load() {
    const id = ++this.requestId;
    this.loading = true;
    const params = {
      sort: SORTS[this.sort],
      page: {
        limit: 60
      }
    };
    if (this.query) params.filter = {
      q: this.query
    };
    app.store.find('hashtags', params).then(hashtags => {
      // A stale response — the user has typed or re-sorted since. Dropping it
      // is what stops the list flickering back to previous results.
      if (id !== this.requestId) return;
      this.hashtags = hashtags;
    }).catch(() => {
      if (id !== this.requestId) return;
      this.hashtags = [];
    }).then(() => {
      if (id !== this.requestId) return;
      this.loading = false;
      m.redraw();
    });
  }
  view() {
    return m(PageStructure, {
      className: "HashtagsPage Page--vertical",
      hero: this.hero.bind(this),
      sidebar: this.sidebar.bind(this)
    }, this.content());
  }
  hero() {
    return m("div", {
      className: "Hero HashtagsHero"
    }, m("div", {
      className: "container"
    }, m("h1", {
      className: "Hero-title"
    }, app.translator.trans('ernestdefoe-hashtags.forum.browse.title')), m("div", {
      className: "Hero-subtitle"
    }, app.translator.trans('ernestdefoe-hashtags.forum.browse.lede'))));
  }
  sidebar() {
    return m(IndexSidebar, null);
  }
  content() {
    return m("div", {
      className: "HashtagsPage-content"
    }, m("div", {
      className: "HashtagsPage-controls"
    }, m("input", {
      className: "FormControl HashtagsPage-search",
      type: "search",
      placeholder: extractText(app.translator.trans('ernestdefoe-hashtags.forum.browse.search_placeholder')),
      value: this.query,
      oninput: e => {
        this.query = e.target.value.trim();
        this.load();
      }
    }), m("div", {
      className: "HashtagsPage-sorts"
    }, Object.keys(SORTS).map(key => m(Button, {
      className: 'Button Button--link' + (this.sort === key ? ' active' : ''),
      onclick: () => {
        if (this.sort === key) return;
        this.sort = key;
        this.load();
      }
    }, app.translator.trans(`ernestdefoe-hashtags.forum.browse.sort_${key}`))))), this.loading ? m(LoadingIndicator, null) : this.hashtags.length === 0 ? m("p", {
      className: "HashtagsPage-empty"
    }, app.translator.trans(this.query ? 'ernestdefoe-hashtags.forum.browse.no_matches' : 'ernestdefoe-hashtags.forum.browse.empty')) : m("ul", {
      className: "HashtagsPage-list"
    }, this.hashtags.map(hashtag => m("li", {
      className: "HashtagsPage-item"
    }, m(Link, {
      className: "HashtagChip",
      href: app.route('hashtag', {
        name: hashtag.nameKey()
      })
    }, m("span", {
      className: "HashtagChip-name"
    }, "#", hashtag.name()), m("span", {
      className: "HashtagChip-count"
    }, hashtag.postCount()))))));
  }
}
flarum.reg.add('ernestdefoe-hashtags', 'forum/components/HashtagsPage', HashtagsPage);